// Libs
import when from 'when'
import Emitter from 'event-emitter'
import glmat from 'gl-matrix'

// Strcutral
import SceneAbs from '../abstract/scene'
import Camera from '../utils/camera'
import TextureLoader from '../utils/textureLoaderObject'
import Node from '../abstract/node'
import Manifest from '../manifest'
import Mouse from '@/js/utils/mouse'

// Entities
import WordFactory from '../entities/wordsFactory'
import Plane from '../entities/plane'

// Post
import PostShake from '../postProcess/shakePostProcess'
import PostMarbling from '../postProcess/marblingDisplacement'
import ShakeMarbling from '../postProcess/shakeMarbling'

// Utils
import RAF from '@/js/utils/raf'
import DebugController from '@/glxp/debug/debugController'

let mat4 = glmat.mat4
let vec3 = glmat.vec3
let vec2 = glmat.vec2

const _VEC2 = vec2.create()
const MARGIN_BOTTOM = 100

var _emitter = {}
Emitter(_emitter)

class Scene extends SceneAbs {
  constructor (container, manager = null) {
    super(container, manager)

    this.manager = manager
    this.camera = new Camera(this, 30)
    this.camera.node.position[2] = -6
    this.shakeFactor = 0
    this.shakeFactorTarget = 0
    this.step = 0
    this.stepTarget = 0
    this.cursor = vec2.create()
    this.cursorTarget = vec2.create()
    this.lastCursor = vec2.create()
    this.velocity = vec2.create()
    this.clearColor = [0, 0, 0, 0]
    this.images = []

    this.mobile = false
    this.loaded = false

    this.textureLoader = new TextureLoader(this)
    this.postMarbling = new PostMarbling(this)

    this.words = []

    this.config = {
      shake_damping: { value: 0.3, range: [0, 0.1] },
      cursor_damping: { value: 0.1, range: [0, 0.1] },
      step_damping: { value: 0.02, range: [0, 0.1] },
      shake_threshold: { value: 0.05, range: [0, 1] },
      word_scale: { value: 18, range: [0, 100] },
      word_scrolling_scale: { value: 5.5, range: [0, 100] },
      // word_spacing: { value: 2.15, range: [0, 10] },
      // word_y_offset: { value: 0.6, range: [-10, 10] },
      word_0_offset: { value: -0.6, range: [-5, 20] },
      word_1_offset: { value: 1.55, range: [-5, 20] },
      word_2_offset: { value: 3.7, range: [-5, 20] }
    }

    DebugController.addConfig(this.config, 'philosophy')
    this.initEvents()
  }

  onLoaded () {
    this.active = true
    this.loaded = true
    this._emitter.emit('loaded')

    let word
    word = new Plane(this, '0', '0', 'unlit')
    word.node.position[2] = -4
    word.originalPos = [0, 0, -4]
    this.words.push(word)

    word = new Plane(this, '1', '1', 'unlit')
    word.node.position[2] = -4
    word.originalPos = [0, 0, -4]
    this.words.push(word)

    word = new Plane(this, '2', '2', 'unlit')
    word.node.position[2] = -4
    word.originalPos = [0, 0, -4]
    this.words.push(word)
  }

  start () {
    // this.shakeFactor = .05
  }

  initEvents () {
    this.container.addEventListener('mousemove', (event) => {
      this.cursorTarget[0] = event.clientX / ((this.width / this.dpr))
      this.cursorTarget[1] = event.clientY / ((this.height / this.dpr))
      // console.log(this.cursorTarget)
    })
  }

  setTitles (images) {
    images.forEach(image => {
      this.images.push({
        url: image,
        options: ['clamp']
      })
    })
  }

  load () {
    let loadables = []

    this.images.forEach((image, key) => {
      loadables.push(this.textureLoader.load(image.url, key, image.options))
    })

    when.all(loadables).then(() => {
      this.onLoaded()
    })

    let percent = 0
    for (let i = 0; i < loadables.length; i++) {
      loadables[i].then(() => {
        percent++
        this._emitter.emit('progress', Math.round((percent / loadables.length) * 100))
      })
    }
  }

  applyDefaultState () {
    let gl = this.gl
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    gl.enable(gl.BLEND)
    gl.enable(gl.DEPTH_TEST)
    // gl.disable(gl.CULL_FACE)
  }

  // Pre-render
  preRender () {
    let gl = this.gl

    this.velocity[0] = this.cursorTarget[0] - this.lastCursor[0]
    this.velocity[1] = this.cursorTarget[1] - this.lastCursor[1]
    this.lastCursor[0] = this.cursorTarget[0]
    this.lastCursor[1] = this.cursorTarget[1]

    vec2.sub(_VEC2, this.cursorTarget, this.cursor)
    vec2.scale(_VEC2, _VEC2, this.config.cursor_damping.value)
    vec2.add(this.cursor, this.cursor, _VEC2)

    this.camera.updateProjection(this.width / this.height)
    this.shakeFactorTarget = Math.max(vec2.length(this.velocity), 0.03)
    let tmp = this.shakeFactorTarget - this.shakeFactor
    tmp *= this.config.shake_damping.value
    this.shakeFactor += tmp
    this.scroll = document.querySelector('.manifest-webgl-container').dataset.scroll / (this.height / this.dpr)
    // this.scroll = (window.pageYOffset || document.documentElement.scrollTop) / (this.height / this.dpr)
    this.camera.node.position[1] = this.scroll * this.config.word_scrolling_scale.value
    this.camera.node.needUpdate = true
  }

  // Render
  render () {
    if (!this.active) { return }
    this.time += RAF.dt / 1000
    this.dt = RAF.dt

    let gl = this.gl

    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)
    if (window.innerWidth > 813) this.preRender()

    gl.viewport(0, 0, this.width, this.height)

    // Render Time
    if (window.innerWidth > 813) {
      this.postMarbling.preRender()
    }
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    let margin = this.config.word_scale.value * 100 * this.dpr
    let spacing = this.config.word_scale.value * 100 * this.dpr
    let m = (margin / this.height)

    for (let i = 0; i < this.words.length; i++) {
      const w = this.words[i]
      // w.node.position[1] = (-i * (this.config.word_spacing.value * this.dpr)) + this.config.word_y_offset.value * this.dpr
      w.node.position[1] = -1 * this.config['word_' + i + '_offset'].value
      w.originalPos[1] = -1 * this.config['word_' + i + '_offset'].value
      w.node.scale[0] = m * 4
      w.node.scale[1] = m
      w.node.needUpdate = true
      w.node.position[0] = 0
      // w.node.position[1] = -1 * this.config['word_' + i + '_offset'].value + Math.cos(this.time)

      w.render()
    }

    if (window.innerWidth > 813) {
      this.postMarbling.postRender()
      this.postMarbling.render()
    }

    this.postRender()
  }

  postRender () {
    this.gl.viewport(0, 0, this.width, this.height)
    // this.grain.render()
  }
}
export default Scene
